import {AddFileDropdownButton} from '@github-ui/code-view-shared/components/AddFileDropdownButton'
import {DuplicateOnKeydownButton} from '@github-ui/code-view-shared/components/DuplicateOnKeydownButton'
import FileResultsList from '@github-ui/code-view-shared/components/files-search/FileResultsList'
import {ReposHeaderRefSelector} from '@github-ui/code-view-shared/components/ReposHeaderRefSelector'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import {
  type DataRouterBlobLayoutPayload,
  type DataRouterFilePagePayload,
  type DataRouterFileTreePagePayload,
  isBlobPayload,
  isDeletePayload,
  isTreePayload,
} from '@github-ui/code-view-types'
import {copyText} from '@github-ui/copy'
import {useCurrentRepository} from '@github-ui/current-repository'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import {repoModelsPromptPath, repositoryTreePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {FindFilesShortcut} from '@github-ui/repos-file-tree-view'
import {ScreenReaderHeading} from '@github-ui/screen-reader-heading'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAlertTooltip} from '@github-ui/use-alert-tooltip'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {KebabHorizontalIcon, PlusIcon, UploadIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Link as PrimerLink} from '@primer/react'
import {clsx} from 'clsx'
import {useRef} from 'react'

import {useCodeNav} from '../../contexts/CodeNavContext'
import {useTreePaneContext} from '../../contexts/TreePaneContext'
import {CurrentBlobProvider} from '../../hooks/CurrentBlob'
import {useCopyRawBlobContents} from '../../hooks/use-copy-raw-blob-contents'
import {KeyboardVisual} from '../../utilities/KeyboardVisual'
import {textAreaId} from '../../utilities/lines'
import {useWorkflowRedirectUrl} from '../blob/BlobNavigationButtons'
import {DeleteDirectoryItem} from '../directory/DeleteDirectoryItem'
import styles from './CodeViewHeader.module.css'
import {DeleteHeaderButtons} from './header-components/DeleteHeaderButtons'
import NavigationMenu from './header-components/NavigationMenu'
import {OptionsElement} from './header-components/SettingsMenuItems'
import {ReposHeaderBreadcrumb} from './ReposHeaderBreadcrumb'

export function CodeViewHeaderDataRouter({
  wideActions,
  narrowActions,
  modelsRepoIntegrationEnabled,
  commitish,
  path,
}: {
  wideActions: React.ReactElement
  narrowActions: React.ReactElement
  modelsRepoIntegrationEnabled?: boolean
  commitish: string
  path: string
}) {
  const {isTreeExpanded, treeToggleElement} = useTreePaneContext()
  const repo = useCurrentRepository()

  const modelsAction =
    modelsRepoIntegrationEnabled && path?.match(/\.prompt\.(yml|yaml)$/) ? (
      <>
        <Button
          as={Link}
          to={repoModelsPromptPath({
            repo,
            path,
            commitish,
            action: 'edit',
          })}
        >
          Edit prompt
        </Button>
      </>
    ) : null

  return (
    <CodeViewHeaderWrapper
      showTree={isTreeExpanded}
      treeToggleElement={treeToggleElement}
      wideActions={
        <>
          {wideActions}
          {modelsAction}
        </>
      }
      narrowActions={
        <>
          {narrowActions}
          {modelsAction}
        </>
      }
    />
  )
}

export function CodeViewHeaderWrapper({
  showTree,
  treeToggleElement,
  wideActions,
  narrowActions,
}: {
  showTree: boolean
  treeToggleElement: React.ReactElement
  wideActions: React.ReactElement
  narrowActions: React.ReactElement
}) {
  const {codeCenterOption} = useCodeViewOptions()

  return (
    <div className={clsx('container', styles.Box)}>
      <div className={styles.StickyHeader} id="StickyHeader">
        <div className={styles.Box_1}>
          <div className={styles.Box_2}>
            {!showTree ? (
              <div className={clsx('react-code-view-header-wrap--narrow', styles.Box_3)}>
                <div
                  className={clsx(
                    styles.treeToggleWrapper,
                    codeCenterOption.enabled && styles.treeToggleWrapperHideOnWide,
                  )}
                >
                  {treeToggleElement}
                </div>
                <div className="react-code-view-header-mb--narrow mr-2">
                  <ReposHeaderRefSelector
                    buttonClassName="ref-selector-class"
                    idEnding="repos-header-ref-selector-wide"
                  />
                </div>
                <div className={clsx('react-code-view-header-mb--narrow', styles.Box_5)}>
                  <ReposHeaderBreadcrumb id="repos-header-breadcrumb" fileNameId="file-name-id" showCopyPathButton />
                </div>
              </div>
            ) : (
              <div className={styles.Box_6}>
                <ReposHeaderBreadcrumb
                  id="repos-header-breadcrumb--wide"
                  fileNameId="file-name-id-wide"
                  showCopyPathButton
                />
              </div>
            )}
            <div className="react-code-view-header-element--wide">
              <div className={styles.Box_7}>
                <div className="d-flex gap-2">{wideActions}</div>
              </div>
            </div>
            <div className="react-code-view-header-element--narrow">
              <div className={styles.Box_7}>
                <div className="d-flex gap-2">{narrowActions}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// this header is now deprecated
export default function CodeViewHeader({
  payload,
  showTree,
  treeToggleElement,
  validCodeNav,
  onFindFilesShortcut,
}: {
  payload: DataRouterFilePagePayload
  showTree: boolean
  treeToggleElement: React.ReactElement
  validCodeNav: boolean
  onFindFilesShortcut?: () => void
}) {
  return (
    <CodeViewHeaderWrapper
      showTree={showTree}
      treeToggleElement={treeToggleElement}
      wideActions={
        <PageActions
          payload={payload}
          onFindFilesShortcut={onFindFilesShortcut}
          narrow={false}
          showTree={showTree}
          validCodeNav={validCodeNav}
        />
      }
      narrowActions={
        <PageActions
          payload={payload}
          onFindFilesShortcut={onFindFilesShortcut}
          narrow
          showTree={showTree}
          validCodeNav={validCodeNav}
        />
      }
    />
  )
}

function PageActions({
  payload,
  onFindFilesShortcut,
  narrow,
  showTree,
  validCodeNav,
}: {
  payload: DataRouterFilePagePayload
  onFindFilesShortcut?: () => void
  narrow: boolean
  showTree: boolean
  validCodeNav: boolean
}) {
  const onCopy = useCopyRawBlobContents()
  const {findFileWorkerPath, githubDevUrl} = useReposAppPayload()
  const repo = useCurrentRepository()
  const path = usePath()
  const {modelsRepoIntegrationEnabled} = useCopilotSettings()
  const refInfo = useRefInfo()
  const {openWithGitHubDevShortcut, openWithGitHubDevInNewWindowShortcut} = useShortcut()
  const inputRef = useRef<HTMLInputElement>(null)
  const showAgentTaskButton = payload.copilotSWEAgentEnabled ?? false

  const goToFileInput = !showTree && (
    <div>
      <FileResultsList
        commitOid={refInfo.currentOid}
        findFileWorkerPath={findFileWorkerPath}
        searchBoxRef={inputRef}
        config={{enableOverlay: true}}
        className={styles.FileResultsList}
      />
      <FindFilesShortcut inputRef={inputRef} onFindFilesShortcut={onFindFilesShortcut} textAreaId={textAreaId} />
    </div>
  )

  return (
    <>
      {isBlobPayload(payload) && (
        <CurrentBlobProvider blob={payload.blob}>
          {!narrow && (
            <>
              <ViewRunsButton />
              {goToFileInput}
            </>
          )}
          <NavigationMenu
            onCopy={onCopy}
            narrow={narrow}
            validCodeNav={validCodeNav}
            copilotAccessAllowed={payload.copilotAccessAllowed ?? false}
          />
        </CurrentBlobProvider>
      )}
      {modelsRepoIntegrationEnabled && payload.path?.match(/\.prompt\.(yml|yaml)$/) && (
        <>
          <Button
            as={Link}
            to={repoModelsPromptPath({
              repo,
              path: payload.path,
              commitish: refInfo.name,
              action: 'edit',
            })}
          >
            Edit prompt
          </Button>
        </>
      )}
      {isTreePayload(payload) && (
        <>
          <ScreenReaderHeading as="h2" text="Directory actions" />
          {!narrow && (
            <>
              {goToFileInput}
              <AddFileDropdownButton />
              {showAgentTaskButton && (
                <NewAgentTaskButton
                  isIconButton
                  currentRepo={repo}
                  currentReference={{
                    path,
                  }}
                  hideRepoSelector
                  eventType="new_agent_task_button_codenav"
                />
              )}
            </>
          )}
          <TreeOverflowMenu narrow={narrow} />
          <PrimerLink
            className="js-github-dev-shortcut d-none"
            data-hotkey={openWithGitHubDevShortcut.hotkey}
            href={githubDevUrl}
          />
          <PrimerLink
            className="js-github-dev-new-tab-shortcut d-none"
            data-hotkey={openWithGitHubDevInNewWindowShortcut.hotkey}
            href={githubDevUrl}
            target="_blank"
          />
        </>
      )}
      {isDeletePayload(payload) && (
        <DeleteHeaderButtons webCommitInfo={payload.webCommitInfo} isBlob={payload.deleteInfo.isBlob} />
      )}
    </>
  )
}

function TreeOverflowMenu({narrow}: {narrow?: boolean}) {
  const path = usePath()
  const refInfo = useRefInfo()
  const {canEdit} = useEditPermissions()
  const repo = useCurrentRepository()
  const {sendRepoClickEvent} = useReposAnalytics()
  const {addToast} = useToastContext()
  const {createPermalink} = useUrlCreator()
  const {copyFilePathShortcut, copyPermalinkShortcut} = useShortcut()
  const {codeCenterOption} = useCodeViewOptions()
  const moreOptionsButtonRef = useRef(null)
  const [updateMessage, clearMessage, portalTooltip] = useAlertTooltip(
    'raw-copy-message-tooltip',
    moreOptionsButtonRef,
    {direction: 'nw'},
  )

  return (
    <>
      {/* Copy link and permalink shortcut */}
      {copyFilePathShortcut.hotkey && (
        <DuplicateOnKeydownButton
          buttonFocusId={textAreaId}
          buttonHotkey={copyFilePathShortcut.hotkey}
          onButtonClick={() => {
            copyText(path)
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'success',
              message: 'Path copied!',
            })
          }}
        />
      )}
      {copyPermalinkShortcut.hotkey && (
        <DuplicateOnKeydownButton
          buttonFocusId={textAreaId}
          buttonHotkey={copyPermalinkShortcut.hotkey}
          onButtonClick={() => {
            copyText(createPermalink({absolute: true}))
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'success',
              message: 'Permalink copied!',
            })
          }}
        />
      )}
      <ScreenReaderHeading as="h2" text="More options" />
      {portalTooltip}
      <ActionMenu
        onOpenChange={open => open && sendRepoClickEvent('MORE_OPTIONS_DROPDOWN')}
        anchorRef={moreOptionsButtonRef}
      >
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            aria-label="More options"
            size="medium"
            title="More options"
            data-testid="tree-overflow-menu-anchor"
            onBlur={clearMessage}
            className={styles.IconButton}
          />
        </ActionMenu.Anchor>

        <ActionMenu.Overlay width="small">
          <ActionList>
            {narrow && canEdit && (
              <>
                <ActionList.LinkItem
                  as={Link}
                  onClick={() => sendRepoClickEvent('NEW_FILE_BUTTON')}
                  to={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'new'})}
                >
                  <ActionList.LeadingVisual>
                    <PlusIcon />
                  </ActionList.LeadingVisual>
                  Create new file
                </ActionList.LinkItem>
                <ActionList.LinkItem
                  onClick={() => sendRepoClickEvent('UPLOAD_FILES_BUTTON')}
                  href={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'upload'})}
                >
                  <ActionList.LeadingVisual>
                    <UploadIcon />
                  </ActionList.LeadingVisual>
                  Upload files
                </ActionList.LinkItem>
                <ActionList.Divider />
              </>
            )}
            <CopyPathsActionItems path={path} updateTooltipMessage={updateMessage} />
            {canEdit && <ActionList.Divider />}
            <DeleteDirectoryItem />
            <ActionList.Divider />
            <ActionList.Group selectionVariant="multiple">
              <ActionList.GroupHeading>View options</ActionList.GroupHeading>
              <OptionsElement option={codeCenterOption} />
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

export function CopyPathsActionItems({
  path,
  updateTooltipMessage,
}: {
  path: string
  updateTooltipMessage: (message: string) => void
}) {
  const {copyFilePathShortcut} = useShortcut()
  const {copyPermalinkShortcut} = useShortcut()
  const {sendRepoClickEvent} = useReposAnalytics()
  const {createPermalink} = useUrlCreator()
  return (
    <>
      <ActionList.Item
        onSelect={() => {
          sendRepoClickEvent('MORE_OPTIONS_DROPDOWN.COPY_PATH')
          copyText(path)
          updateTooltipMessage('Path copied!')
        }}
      >
        Copy path
        {copyFilePathShortcut.hotkey && (
          <ActionList.TrailingVisual aria-hidden="true" className="d-flex">
            <KeyboardVisual shortcut={copyFilePathShortcut} />
          </ActionList.TrailingVisual>
        )}
      </ActionList.Item>
      <ActionList.Item
        onSelect={() => {
          sendRepoClickEvent('MORE_OPTIONS_DROPDOWN.COPY_PERMALINK')
          copyText(createPermalink({absolute: true}))
          updateTooltipMessage('Permalink copied!')
        }}
      >
        Copy permalink
        {copyPermalinkShortcut.hotkey && (
          <ActionList.TrailingVisual aria-hidden="true" className="d-flex">
            <KeyboardVisual shortcut={copyPermalinkShortcut} />
          </ActionList.TrailingVisual>
        )}
      </ActionList.Item>
    </>
  )
}

function ViewRunsButton() {
  const redirectUrl = useWorkflowRedirectUrl()

  if (!redirectUrl) {
    return null
  }

  return (
    <Button as={Link} to={redirectUrl}>
      View Runs
    </Button>
  )
}

function GoToFileInput({payload}: {payload: DataRouterFilePagePayload}) {
  const {isTreeExpanded, onFindFilesShortcut} = useTreePaneContext()
  const {findFileWorkerPath} = useReposAppPayload()
  const inputRef = useRef<HTMLInputElement>(null)

  if (isTreeExpanded) {
    return null
  }

  return (
    <div>
      <FileResultsList
        commitOid={payload.refInfo.currentOid}
        findFileWorkerPath={findFileWorkerPath}
        searchBoxRef={inputRef}
        config={{enableOverlay: true}}
        className={styles.FileResultsList}
      />
      <FindFilesShortcut inputRef={inputRef} onFindFilesShortcut={onFindFilesShortcut} textAreaId={textAreaId} />
    </div>
  )
}

export function BlobHeaderActions({payload, narrow}: {payload: DataRouterBlobLayoutPayload; narrow: boolean}) {
  const onCopy = useCopyRawBlobContents()
  const {validCodeNav} = useCodeNav()

  return (
    <>
      {!narrow && (
        <>
          <ViewRunsButton />
          <GoToFileInput payload={payload} />
        </>
      )}
      <NavigationMenu
        onCopy={onCopy}
        narrow={narrow}
        validCodeNav={validCodeNav}
        copilotAccessAllowed={payload.copilotAccessAllowed ?? false}
      />
    </>
  )
}

export function TreeHeaderActions({
  narrow,
  payload,
  path,
}: {
  narrow: boolean
  payload: DataRouterFileTreePagePayload
  path: string
}) {
  const {githubDevUrl} = useReposAppPayload()
  const repo = useCurrentRepository()
  const showAgentTaskButton = payload.copilotSWEAgentEnabled ?? false
  const {openWithGitHubDevShortcut, openWithGitHubDevInNewWindowShortcut} = useShortcut()

  return (
    <>
      <ScreenReaderHeading as="h2" text="Directory actions" />
      {!narrow && (
        <>
          <GoToFileInput payload={payload} />
          <AddFileDropdownButton />
          {showAgentTaskButton && (
            <NewAgentTaskButton
              isIconButton
              currentRepo={repo}
              currentReference={{
                path,
              }}
              hideRepoSelector
              eventType="new_agent_task_button_codenav"
            />
          )}
        </>
      )}
      <TreeOverflowMenu narrow={narrow} />
      <PrimerLink
        className="js-github-dev-shortcut d-none"
        data-hotkey={openWithGitHubDevShortcut.hotkey}
        href={githubDevUrl}
      />
      <PrimerLink
        className="js-github-dev-new-tab-shortcut d-none"
        data-hotkey={openWithGitHubDevInNewWindowShortcut.hotkey}
        href={githubDevUrl}
        target="_blank"
      />
    </>
  )
}
